import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle, Database, HardDrive, RefreshCw, Trash2, Clock, AlertTriangle, Activity, Folder } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SystemHealthPanel = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API}/system/health`);
      setHealth(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); const i = setInterval(fetchHealth, 30000); return () => clearInterval(i); }, []);

  const runCleanup = async (dryRun) => {
    setCleanupRunning(true);
    try {
      const res = await axios.post(`${API}/system/cleanup?dry_run=${dryRun}`);
      setCleanupResult(res.data);
      await fetchHealth();
    } catch (err) {
      setError(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupRunning(false);
    }
  };

  const formatBytes = (b) => b >= 1024**3 ? `${(b/1024**3).toFixed(2)} GB` : b >= 1024**2 ? `${(b/1024**2).toFixed(2)} MB` : `${(b/1024).toFixed(2)} KB`;
  const riskColor = { ok:'text-green-400', warning:'text-yellow-400', critical:'text-orange-400', emergency:'text-red-400' };
  const riskBg = { ok:'bg-green-500/20 border-green-500/30', warning:'bg-yellow-500/20 border-yellow-500/30', critical:'bg-orange-500/20 border-orange-500/30', emergency:'bg-red-500/20 border-red-500/30' };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6" data-testid="system-health-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"><Activity className="text-blue-500" size={20} /></div>
          <div><h2 className="text-xl font-bold">System Health</h2><p className="text-zinc-500 text-sm">Monitor disk and database</p></div>
        </div>
        <button onClick={fetchHealth} className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg"><RefreshCw size={16} /> Refresh</button>
      </div>

      {health?.uploads_paused && <div className={`p-4 rounded-lg border ${riskBg[health.risk_level]||''}`}><AlertTriangle className={riskColor[health.risk_level]} size={20} /> <span className={riskColor[health.risk_level]}>{health.read_only_mode ? 'Read-Only Mode' : 'Uploads Paused'} - {health.disk?.percent_used}%</span></div>}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-dark p-6 border border-zinc-700">
          <div className="flex items-center gap-2 mb-4"><HardDrive className="text-blue-400" size={20} /><h3 className="font-semibold">Disk</h3><span className={`ml-auto px-2 py-1 rounded text-xs ${riskBg[health?.risk_level]||''}`}>{health?.risk_level?.toUpperCase()}</span></div>
          <div className="w-full bg-zinc-700 rounded-full h-4 mb-4"><div className={`h-4 rounded-full ${health?.disk?.percent_used>=95?'bg-red-500':health?.disk?.percent_used>=90?'bg-orange-500':health?.disk?.percent_used>=80?'bg-yellow-500':'bg-green-500'}`} style={{width:`${health?.disk?.percent_used||0}%`}}/></div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="bg-zinc-800/50 rounded p-2 text-center"><div className="text-zinc-400 text-xs">Total</div><div>{health?.disk?.total_gb} GB</div></div>
            <div className="bg-zinc-800/50 rounded p-2 text-center"><div className="text-zinc-400 text-xs">Used</div><div className="text-orange-400">{health?.disk?.used_gb} GB</div></div>
            <div className="bg-zinc-800/50 rounded p-2 text-center"><div className="text-zinc-400 text-xs">Free</div><div className="text-green-400">{health?.disk?.free_gb} GB</div></div>
          </div>
        </div>

        <div className="card-dark p-6 border border-zinc-700">
          <div className="flex items-center gap-2 mb-4"><Database className="text-green-400" size={20} /><h3 className="font-semibold">MongoDB</h3><span className={`ml-auto px-2 py-1 rounded text-xs ${health?.mongodb?.connected?'bg-green-500/20 text-green-400':'bg-red-500/20 text-red-400'}`}>{health?.mongodb?.connected?'OK':'DOWN'}</span></div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-400">Ping</span>{health?.mongodb?.ping_success?<CheckCircle className="text-green-400" size={16}/>:<AlertCircle className="text-red-400" size={16}/>}</div>
            {health?.mongodb?.latency_ms&&<div className="flex justify-between"><span className="text-zinc-400">Latency</span><span>{health.mongodb.latency_ms}ms</span></div>}
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-700 text-sm"><div className="flex justify-between"><span className="text-zinc-400">Last Cleanup</span><span>{health?.last_cleanup?.time?new Date(health.last_cleanup.time).toLocaleString():'Never'}</span></div></div>
        </div>
      </div>

      <div className="card-dark p-6 border border-zinc-700">
        <div className="flex items-center gap-2 mb-4"><Folder className="text-yellow-400" size={20} /><h3 className="font-semibold">Top Directories</h3></div>
        <table className="w-full text-sm"><thead><tr className="text-zinc-400 border-b border-zinc-700"><th className="text-left py-2">Path</th><th className="text-right py-2">Size</th></tr></thead>
        <tbody>{health?.largest_directories?.slice(0,5).map((d,i)=><tr key={i} className="border-b border-zinc-800"><td className="py-2 font-mono text-xs">{d.path}</td><td className="py-2 text-right text-yellow-400">{d.size_display}</td></tr>)}</tbody></table>
      </div>

      <div className="card-dark p-6 border border-zinc-700">
        <div className="flex items-center gap-2 mb-4"><Trash2 className="text-orange-400" size={20} /><h3 className="font-semibold">Cleanup</h3></div>
        <div className="flex gap-4 mb-4">
          <button onClick={()=>runCleanup(true)} disabled={cleanupRunning} className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg disabled:opacity-50"><Clock size={16}/>Preview</button>
          <button onClick={()=>runCleanup(false)} disabled={cleanupRunning} className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded-lg disabled:opacity-50"><Trash2 size={16}/>Run Now</button>
        </div>
        {cleanupResult&&<div className={`p-3 rounded-lg border ${cleanupResult.success?'bg-green-500/10 border-green-500/30':'bg-red-500/10 border-red-500/30'}`}><span className={cleanupResult.success?'text-green-400':'text-red-400'}>{cleanupResult.dry_run?'Preview':'Done'}: {cleanupResult.summary?.total_files_deleted} files, {cleanupResult.summary?.bytes_display}</span></div>}
      </div>
    </div>
  );
};

export default SystemHealthPanel;
